import { useCallback, useEffect, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { getForms, removeInstance } from "./repositories/FormsRepository";

import { Paper, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";

import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowClickedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";

import { CreateForm } from "./components/CreateForm";
import { EditInstance } from "./components/EditInstance";
import { Filters } from "./filters/Filters";

import { CustomStatusPanel } from "@/controls/ag-grid/components/CustomStatusPanel";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	ErrorModal,
	getMomentTz,
	MesfModal,
	useContextMenuMESF,
} from "@dexteel/mesf-core";
import { useQuery } from "@tanstack/react-query";
import { Moment } from "moment";
import { useSearchParams } from "react-router-dom";
import { useFormInstanceOptionsFunctions } from "./hooks/useFormInstanceOptionsFunctions";
import { useTableData } from "./hooks/useTableData";
import { FormInstance, FormInstanceResponse } from "./models/FormInstance";

const moment = getMomentTz();

type useFormsIntancesProps = {
	start: Date;
	end: Date;
	formInstanceStatusCodes: string | null;
};

export const useFormsInstances = ({
	start,
	end,
	formInstanceStatusCodes,
}: useFormsIntancesProps) => {
	return useQuery<FormInstanceResponse[], Error>({
		queryKey: ["formsInstances", start, end, formInstanceStatusCodes],
		queryFn: ({ signal }) =>
			getForms(
				{
					start,
					end,
					formInstanceStatusCodes,
				},
				signal,
			),
	});
};

export const FormsInstancePage = ({
	formId,
}: {
	formId: string | undefined;
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const urlStatusCode = searchParams.get("status") ?? "";
	const urlStartDate = searchParams.get("start") ?? "";
	const urlEndDate = searchParams.get("end") ?? "";

	const [error, setError] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [startDate, setStartDate] = useState<Moment>(
		moment(urlStartDate) || moment().startOf("day"),
	);
	const [endDate, setEndDate] = useState<Moment>(
		moment(urlEndDate) || moment().endOf("day"),
	);
	const [filterValue, setFilterValue] = useState("");
	const [statusValue, setStatusValue] = useState(urlStatusCode);
	const [selectedRowData, setSelectedRowData] = useState<FormInstance | null>(
		null,
	);
	const [showModal, setShowModal] = useState<
		"create" | "edit" | "view" | "delete" | ""
	>("");

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		refetch,
	} = useFormsInstances({
		start: startDate.toDate(),
		end: endDate.toDate(),
		formInstanceStatusCodes: null,
	});

	const downloadPDFs = async (formInstance: FormInstance) => {
		const baseURL = `${window.location.protocol}//${window.location.hostname}${
			window.location.port ? ":" + window.location.port : ""
		}`;
		if (formInstance) {
			const form = "pdf";
			const downloadLink = document.createElement("a");
			downloadLink.href = `${baseURL}/${form}/${formInstance.FormInstanceId}`;
			downloadLink.download = `${formInstance.AssetId}_${formInstance.OpenTimestamp}.pdf`;
			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);
			await new Promise((resolve) => setTimeout(resolve, 1000));
		}
	};

	const deleteForms = async (formInstance: FormInstance) => {
		removeInstance(formInstance?.FormInstanceId!).then((res: any) => {
			if (res.ok) {
				refetch();
			} else {
				setShowModal("");
				refetch();

				setError((prev) => {
					if (prev) {
						return `${prev}. ${res.message}`;
					} else {
						return res.message;
					}
				});
			}
		});
	};

	const { defaultColDef } = useGridDefinitions({});

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { columnDefs } = useTableData({ showContextMenu });

	const { getMenuOptions } = useFormInstanceOptionsFunctions({
		setSelectedRowData: setSelectedRowData,
		setShowModal: setShowModal,
		deleteForms: (formInstance) => deleteForms(formInstance),
		dowloandPdfs: (formInstance) => downloadPDFs(formInstance),
	});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "formInstanceContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onRowClicked = (params: RowClickedEvent) => {
		gridApi?.deselectAll();
		params.node.setSelected(true);
	};

	const onDoubleClicked = (params: RowDoubleClickedEvent) => {
		setSelectedRowData(params.data);
		setShowModal(
			(params.data as FormInstance).SubmitTimestamp ? "view" : "edit",
		);
	};

	const resetFilter = () => {
		gridApi?.setGridOption("quickFilterText", "");
		setFilterValue("");
		// status
		setStatusValue("All");
		searchParams.set("status", "All");
		// start
		setStartDate(moment().clone().subtract(1, "month"));
		searchParams.set(
			"start",
			encodeURIComponent(
				moment().clone().subtract(1, "month")!.toDate().toISOString(),
			),
		);
		// end
		setEndDate(moment().endOf("day"));
		searchParams.set(
			"end",
			encodeURIComponent(moment().endOf("day")!.toDate().toISOString()),
		);
		setSearchParams(searchParams, { replace: true });
	};

	useEffect(() => {
		if (!isLoading) {
			if (formId) {
				if (formId === "new") {
				} else {
					const formNode = gridApi?.getRowNode(formId);

					if (formNode) {
						gridApi?.ensureNodeVisible(formNode);

						formNode.setSelected(true);
						setSelectedRowData(formNode.data);
						setShowModal("edit");
					}
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		let shouldUpdate = false;

		if (searchParams.has("start")) {
			const start = searchParams.get("start");
			if (start === "") {
				searchParams.delete("start");
				shouldUpdate = true;
			}
			setStartDate(moment(decodeURIComponent(start!)));
		}
		if (searchParams.has("end")) {
			const end = searchParams.get("end");
			setEndDate(moment(decodeURIComponent(end!)));
		}
		if (searchParams.has("status")) {
			const status = searchParams.get("status");
			setStatusValue(status ?? "All");
		}

		// to initialize
		if (!searchParams.has("start")) {
			const startOneMonthAgo = moment().clone().subtract(1, "month");
			searchParams.set(
				"start",
				encodeURIComponent(startOneMonthAgo.toDate().toISOString()),
			);
			shouldUpdate = true;
		}
		if (!searchParams.has("end")) {
			searchParams.set(
				"end",
				encodeURIComponent(moment()!.toDate().toISOString()),
			);
			shouldUpdate = true;
		}
		if (!searchParams.has("status")) {
			searchParams.set("status", "All");
			shouldUpdate = true;
		}

		if (shouldUpdate) {
			setSearchParams(searchParams, { replace: true });
		}
	}, [searchParams]);

	useEffect(() => {
		registerConfig({
			id: "formInstanceContext",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (isError) {
			setError(e.message);
		}
	}, [isError]);

	return (
		<>
			<Grid2 container style={{ padding: 20 }}>
				<Grid2
					container
					alignItems="center"
					justifyContent="center"
					spacing={3}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Typography
							variant="h5"
							style={{
								margin: "0",
								fontWeight: 600,
								marginLeft: 5,
							}}
						>
							FORMS
						</Typography>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Paper elevation={1} style={{ padding: 10, marginBottom: 20 }}>
							<Grid2 container spacing={2} alignItems="center">
								<Grid2 size={{ xs: 12, sm: 12, md: 10, lg: 10 }}>
									<Filters
										startDate={startDate}
										endDate={endDate}
										filterValue={filterValue}
										statusValue={statusValue}
										setStartDate={(startDate) => {
											searchParams.set(
												"start",
												encodeURIComponent(startDate!.toDate().toISOString()),
											);
											setSearchParams(searchParams, { replace: true });
											setStartDate(startDate);
										}}
										setEndDate={(endDate) => {
											searchParams.set(
												"end",
												encodeURIComponent(endDate!.toDate().toISOString()),
											);
											setSearchParams(searchParams, { replace: true });
											setEndDate(endDate);
										}}
										setFilterValue={(value) => {
											setFilterValue(value);
											gridApi?.setGridOption("quickFilterText", value);
										}}
										setStatusValue={(value) => {
											searchParams.set("status", value);
											setSearchParams(searchParams, { replace: true });
											setStatusValue(value);
										}}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, sm: 12, md: 2, lg: 2 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={resetFilter}
										size="medium"
									>
										RESET
									</Button>
								</Grid2>
							</Grid2>
						</Paper>
					</Grid2>
				</Grid2>

				<Paper
					elevation={2}
					style={{
						height: "61vh",
						width: "100%",
					}}
				>
					<AgGridReact<FormInstanceResponse>
						loading={isLoading}
						onGridReady={(params) => setGridApi(params.api)}
						rowData={rows?.filter((f) =>
							statusValue === "All"
								? true
								: f.FormInstanceStatusCode === statusValue,
						)}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						rowHeight={38}
						headerHeight={32}
						animateRows
						loadingOverlayComponent={CenteredLazyLoading}
						getContextMenuItems={getContextMenuItems}
						onRowDoubleClicked={onDoubleClicked}
						onRowClicked={onRowClicked}
						onCellClicked={(params) => {
							if (params.column.getColId() !== "") {
								params.node.setSelected(!params.node.isSelected());
							}
						}}
						getRowId={(params) => String(params.data.FormInstanceId)}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
							statusBar: {
								statusPanels: [
									{
										statusPanel: "agTotalRowCountComponent",
										align: "right",
									},
									{
										statusPanel: (params: IStatusPanelParams) => (
											<CustomStatusPanel
												handleNew={() => {
													setSelectedRowData(null);
													setShowModal("create");
												}}
											/>
										),
										align: "left",
									},
								],
							},
						}}
					/>
				</Paper>
			</Grid2>

			<CreateForm
				show={showModal === "create"}
				onHide={async (id?: number | null) => {
					await refetch();
					gridApi?.deselectAll();
					setSelectedRowData(null);
					setShowModal("");

					if (id) {
						setTimeout(() => {
							const rowNode = gridApi?.getRowNode(String(id));

							if (rowNode!) {
								gridApi?.ensureNodeVisible(rowNode);
								rowNode.setSelected(true);
								setSelectedRowData(rowNode.data);
								setShowModal("edit");
							}
						}, 0);
						return;
					}
				}}
			/>
			<EditInstance
				show={showModal}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) refetch();
					gridApi?.deselectAll();
					setSelectedRowData(null);
					setShowModal("");
				}}
				refetchForms={refetch}
				formInstanceId={selectedRowData?.FormInstanceId!}
			/>
			<MesfModal
				open={showModal === "delete"}
				handleClose={() => setShowModal("")}
				title="WARNING"
				key="delete_modal"
			>
				<MesfModal.Content>
					<Typography>
						Be careful, once deleted you will not be able to recover this form
						instance, are you sure you want to delete it?
					</Typography>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "16px 24px" }}>
					<Button onClick={() => setShowModal("")} autoFocus color="inherit">
						CANCEL
					</Button>
					<Button autoFocus color="secondary" onClick={() => {}}>
						Delete
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
